import { AppsFlyer } from '../plugins/apps_flyer_plugin.js';
import { VersionLoader } from '../version_loader.js';
import { analytics, AnalyticsEventName, AnalyticsPropertyKey } from './index.js';

function shouldLog(): boolean
{
  const cap: any = ( window as any ).Capacitor;
  const platform = cap?.getPlatform?.();
  return platform === 'android' || platform === 'ios';
}

export type AppOpenSource =
  | 'icon'
  | 'push_notification'
  | 'deep_link'
  | 'store'
  | 'unknown';

// export function maybeLogRageQuitAfterAdOffer(): void
// {
//   const now_ms = performance.now();
//   if ( reward_offer_shown_at_ms == null )
//   {
//     return;
//   }

//   if ( isRageQuitAfterAdOfferSuppressed( now_ms ) )
//   {
//     resetRageQuitAfterAdOfferState();
//     return;
//   }

//   const elapsed_ms = now_ms - reward_offer_shown_at_ms;
//   const should_log = !reward_offer_interacted && elapsed_ms >= 0 && elapsed_ms <= RAGE_QUIT_WINDOW_MS;

//   resetRageQuitAfterAdOfferState();

//   if ( should_log )
//   {
//     void logRageQuitAfterAdOffer();
//     analytics.track( AnalyticsEventName.RAGE_QUIT_AFTER_AD_OFFER, {
//       [AnalyticsPropertyKey.TIMESTAMP]: new Date().toISOString(),
//     } );
//   }
// }

const RAGE_QUIT_WINDOW_MS = 10_000;

let reward_offer_shown_at_ms: number | null = null;
let reward_offer_interacted = false;
let rage_quit_after_ad_offer_suppressed_until_ms = 0;

function getStoredRewardOfferClickedTotal(): number
{
  try
  {
    const raw = localStorage.getItem( "reward_offer_clicked_total" );
    const value = Number( raw );
    return Number.isFinite( value ) && value >= 0 ? value : 0;
  }
  catch
  {
    return 0;
  }
}

function getStoredRewardOfferShownTotal(): number
{
  try
  {
    const raw = localStorage.getItem( "reward_offer_shown_total" );
    const value = Number( raw );
    return Number.isFinite( value ) && value >= 0 ? value : 0;
  }
  catch
  {
    return 0;
  }
}

function getStoredRewardCompletedTotal(): number
{
  try
  {
    const raw = localStorage.getItem( "reward_completed_total" );
    const value = Number( raw );
    return Number.isFinite( value ) && value >= 0 ? value : 0;
  }
  catch
  {
    return 0;
  }
}

function setStoredRewardOfferClickedTotal( value: number ): void
{
  try
  {
    localStorage.setItem( "reward_offer_clicked_total", String( value ) );
  }
  catch ( e ) 
  {
    console.warn( "Failed to store reward_offer_clicked_total", e );
  }
}

function setStoredRewardOfferShownTotal( value: number ): void
{
  try
  {
    localStorage.setItem( "reward_offer_shown_total", String( value ) );
  }
  catch ( e ) 
  {
    console.warn( "Failed to store reward_offer_shown_total", e );
  }
}

function setStoredRewardCompletedTotal( value: number ): void
{
  try
  {
    localStorage.setItem( "reward_completed_total", String( value ) );
  }
  catch ( e ) 
  {
    console.warn( "Failed to store reward_completed_total", e );
  }
}

function getLevelMilestones( from_level: number, to_level: number ): number[]
{
  const from = Math.max( 0, Math.floor( from_level ) );
  const to = Math.max( 0, Math.floor( to_level ) );
  if ( to <= from ) return [];

  const out: number[] = [];
  for ( let lvl = from + 1; lvl <= to; lvl++ )
  {
    if ( lvl <= 20 ) out.push( lvl );
    else if ( lvl % 5 === 0 ) out.push( lvl );
  }
  return out;
}

function getRoundMilestones( from_rounds: number, to_rounds: number ): number[]
{
  const from = Math.max( 0, Math.floor( from_rounds ) );
  const to = Math.max( 0, Math.floor( to_rounds ) );
  if ( to <= from ) return [];

  const out: number[] = [];
  for ( let r = from + 1; r <= to && r <= 20; r++ )
  {
    out.push( r );
  }
  return out;
}

export function shouldLogMissionLevelCompleteMilestone( mission_lvl: number ): boolean
{
  if ( mission_lvl <= 40 ) return true;
  return mission_lvl <= 60 && mission_lvl % 5 === 0;
}

export async function logAfCompleteTutorial(): Promise<void>
{
  if ( !shouldLog() ) return;
  await AppsFlyer.logEvent( 'tutorial_complete');
}

export async function logRoundsPlayedMilestones( previous_rounds: number, current_rounds: number ): Promise<void>
{
  if ( !shouldLog() ) return;
  const milestones = getRoundMilestones( previous_rounds, current_rounds );
  for ( const rounds of milestones )
  {
    await AppsFlyer.logEvent( 'Rounds_played', { rounds } );
    await AppsFlyer.logEvent( `Rounds_played_${ rounds }`);
  }
}

export async function logAfAppOpen( source: AppOpenSource ): Promise<void>
{
  if ( !shouldLog() ) return;
  const platform = window.Capacitor?.getPlatform?.() ?? 'unknown';
  await AppsFlyer.logEvent( 'app_open', {
    platform,
    source,
    app_version: VersionLoader.getVersion(),
  } );
}

export async function logAfSessionMinuteMilestone( minute: number ): Promise<void>
{
  if ( !shouldLog() ) return;
  await AppsFlyer.logEvent( `session_${minute}min` );
}

export async function logMenuScreenClickOnPromoGame( promo: string ): Promise<void>
{
  if ( !shouldLog() ) return;
  await AppsFlyer.logEvent( 'Menu_Screen_click_promo_game', { promo } );
}

export async function logMoreGamesDialogClickOnPromoGame( promo: string ): Promise<void>
{
  if ( !shouldLog() ) return;
  await AppsFlyer.logEvent( 'More_Games_Dialog_click_promo_game', { promo } );
}

export function incrementRewardOfferClickedTotal(): number
{
  const total_clicks = getStoredRewardOfferClickedTotal() + 1;
  setStoredRewardOfferClickedTotal( total_clicks );
  return total_clicks;
}

export function incrementRewardOfferShownTotal(): number
{
  const total_shown = getStoredRewardOfferShownTotal() + 1;
  setStoredRewardOfferShownTotal( total_shown );
  return total_shown;
}

export async function logRewardOfferShownTotal( total_shown = incrementRewardOfferShownTotal() ): Promise<void>
{
  if ( !shouldLog() ) return;
  await AppsFlyer.logEvent( 'reward_offer_shown_total', { total_shown } );
}

export function incrementRewardCompletedTotal(): number
{
  const total_completed = getStoredRewardCompletedTotal() + 1;
  setStoredRewardCompletedTotal( total_completed );
  return total_completed;
}

export async function logAdRewardedStarted( placement = 'rewarded' ): Promise<void>
{
  if ( !shouldLog() ) return;
  await AppsFlyer.logEvent( 'ad_rewarded_started', { placement } );
}

export async function logSkippedRewarded(): Promise<void>
{
  if ( !shouldLog() ) return;
  await AppsFlyer.logEvent( 'skipped_rewarded' );
}

export async function logRageQuitAfterAdOffer(): Promise<void>
{
  if ( !shouldLog() ) return;
  await AppsFlyer.logEvent( 'rage_quit_after_ad_offer' );
}

export function suppressRageQuitAfterAdOfferForInterstitial( duration_ms = RAGE_QUIT_WINDOW_MS ): void
{
  const safe_duration_ms = Math.max( 0, duration_ms );
  rage_quit_after_ad_offer_suppressed_until_ms = performance.now() + safe_duration_ms;
}

export interface GameStartEventProperties
{
  mode: string;
  level: number;
  session_id: string;
}

export async function logGameStart( properties: GameStartEventProperties ): Promise<void>
{
  if ( !shouldLog() ) return;
  await AppsFlyer.logEvent( 'game_start', properties );
}

export interface GameExitEventProperties
{
  reason: string;
  elapsed_time: number;
  level: number;
}

export async function logGameExit( properties: GameExitEventProperties ): Promise<void>
{
  if ( !shouldLog() ) return;
  await AppsFlyer.logEvent( 'game_exit', properties );
}

export interface GameEndEventProperties
{
  result: 'win' | 'lose';
  score: number;
  duration: number;
}

export async function logGameEnd( properties: GameEndEventProperties ): Promise<void>
{
  if ( !shouldLog() ) return;
  await AppsFlyer.logEvent( 'game_end', properties );
}

export interface LevelCompletedEventProperties
{
  level: number;
  score: number;
  attempts: number;
}

export async function logLevelCompleted( properties: LevelCompletedEventProperties ): Promise<void>
{
  if ( !shouldLog() ) return;
  await AppsFlyer.logEvent( 'level_completed', properties );
}

export async function logCalendarDayCompleted( day: number ): Promise<void>
{
  if ( !shouldLog() ) return;
  await AppsFlyer.logEvent( `calendar_day_${day}_completed` );
}

export async function logClickedOnRewarded( total_clicks = incrementRewardOfferClickedTotal() ): Promise<void>
{
  if ( !shouldLog() ) return;
  await AppsFlyer.logEvent( 'reward_offer_clicked_total', { total_clicks } );
  await AppsFlyer.logEvent( 'clicked_on_rewarded' );
}

export function markRewardOfferInteracted(): void
{
  if ( reward_offer_shown_at_ms == null ) return;
  reward_offer_interacted = true;
}

export async function logRewardedImpression(): Promise<void>
{
  if ( !shouldLog() ) return;
  await AppsFlyer.logEvent( 'rewarded_impression' );
}

export interface RewardedCompletedEventProperties
{
  placement: string;
  reward_type: string;
  reward_value: number;
}

export async function logRewardedCompleted( properties: RewardedCompletedEventProperties ): Promise<void>
{
  if ( !shouldLog() ) return;
  await AppsFlyer.logEvent( 'rewarded_completed', properties );
}

export interface BoosterRewardedFunnelEventProperties
{
  placement: string;
  source: string;
  booster_type: string;
  reward_value: number;
  status?: string;
  close_reason?: string;
  shown?: boolean;
  rewarded?: boolean;
  idempotent?: boolean;
}

export async function logBoosterRewardedFunnelEvent(
  event_name:
    | AnalyticsEventName.BOOSTER_REWARD_OFFER_SHOWN
    | AnalyticsEventName.BOOSTER_REWARD_OFFER_CLICKED
    | AnalyticsEventName.BOOSTER_REWARD_AD_RESULT
    | AnalyticsEventName.BOOSTER_REWARD_GRANTED
    | AnalyticsEventName.BOOSTER_REWARD_OFFER_CLOSED,
  properties: BoosterRewardedFunnelEventProperties
): Promise<void>
{
  analytics.track( event_name, {
    [AnalyticsPropertyKey.PLACEMENTID]: properties.placement,
    source: properties.source,
    booster_type: properties.booster_type,
    reward_value: properties.reward_value,
    ...( properties.status ? { status: properties.status } : {} ),
    ...( properties.close_reason ? { close_reason: properties.close_reason } : {} ),
    ...( typeof properties.shown === 'boolean' ? { shown: properties.shown } : {} ),
    ...( typeof properties.rewarded === 'boolean' ? { rewarded: properties.rewarded } : {} ),
    ...( typeof properties.idempotent === 'boolean' ? { idempotent: properties.idempotent } : {} ),
  } );

  if ( !shouldLog() ) return;
  await AppsFlyer.logEvent( event_name, properties );
}

export interface VirtualCurrencyEarnedEventProperties
{
  currency_name: string;
  amount: number;
  source: string;
}

export async function logVirtualCurrencyEarned( properties: VirtualCurrencyEarnedEventProperties ): Promise<void>
{
  if ( !shouldLog() || properties.amount <= 0 ) return;
  await AppsFlyer.logEvent( 'virtual_currency_earned', properties );
}

export interface VirtualCurrencySpentEventProperties
{
  currency_name: string;
  amount: number;
  sink: string;
}

export async function logVirtualCurrencySpent( properties: VirtualCurrencySpentEventProperties ): Promise<void>
{
  if ( !shouldLog() || properties.amount <= 0 ) return;
  await AppsFlyer.logEvent( 'virtual_currency_spent', properties );
}

export interface InterstitialShownEventProperties
{
  placement: string;
  network: string;
}

export async function logInterstitialShown( properties: InterstitialShownEventProperties ): Promise<void>
{
  if ( !shouldLog() ) return;
  await AppsFlyer.logEvent( 'interstitial_shown', properties );
}

export async function logRewardCompletedTotal( total_completed = incrementRewardCompletedTotal() ): Promise<void>
{
  if ( !shouldLog() ) return;
  await AppsFlyer.logEvent( 'reward_completed_total', { total_completed } );
}
